import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { equals, isEmpty, o } from 'ramda';

// Utilities
import { Box, useMediaQuery, useTheme } from '@mui/material';
import {
  cardsListStyles,
  cardsPanelContainerStyles,
  BASE_AND_CUSTOM_TABS as tabs,
} from 'feature/loading-management/utils/const';
import { useSetLoadingTitle } from 'hooks';

// Components
import CardSkeletonList from 'feature/loading-management/card-skeleton/CardSkeletonList';
import {
  Heading,
  ListTabs,
  NoAuthorizationsAvailable,
} from 'feature/loading-management/shared-components';
import HeaderButtons from './HeaderButtons';
import CardsList from './CardsList';
import FiltersContainer from '../filter-popover/FilterContainer';

const CardsPanel = ({
  activeMyAuthTab,
  currentSelection,
  filtersOpen,
  filterIdentifierCount,
  myAuthorizations,
  myAuthorizationCards,
  myAuthorizationPending,
  resetCardFilters,
  selectorsListPending,
  setActiveMyAuthTab,
  setFiltersOpen,
  viewBy,
}) => {
  const { breakpoints } = useTheme();
  const lgUp = useMediaQuery(breakpoints.up('lg'), { noSsr: true });

  const filtersRef = useRef();

  const [selected, setSelected] = useState('');
  const title = useSetLoadingTitle(selectorsListPending, currentSelection);

  useEffect(() => {
    // If name short is not undefined and is different then state val set to new value
    if (currentSelection.nameShort && !equals(selected, currentSelection.nameShort)) {
      setSelected(currentSelection.nameShort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMyAuthTab, selected, currentSelection.nameShort]);

  const showTabs = viewBy !== 'product';

  // handleChange :: Value --> setActiveTab
  const handleTabChange = o(resetCardFilters, setActiveMyAuthTab);
  const labelCounts =
    !isEmpty(myAuthorizations) && !Array.isArray(myAuthorizations) ? myAuthorizations : {};

  const renderCards = () => {
    if (myAuthorizationPending) return <CardSkeletonList />;
    if (isEmpty(myAuthorizationCards) || !myAuthorizationCards?.length)
      return <NoAuthorizationsAvailable />;
    return <CardsList cards={myAuthorizationCards} viewBy={viewBy} />;
  };

  return (
    <Box {...cardsPanelContainerStyles}>
      <Heading
        headerButtons={
          <HeaderButtons
            filterIdentifierCount={filterIdentifierCount}
            filtersOpen={filtersOpen}
            filtersRef={filtersRef}
            pending={selectorsListPending}
            setFiltersOpen={setFiltersOpen}
          />
        }
        listTabs={
          <ListTabs
            activeTab={activeMyAuthTab}
            labelCounts={labelCounts}
            onChange={handleTabChange}
            pendingState={myAuthorizationPending}
            tabs={tabs}
          />
        }
        showTabs={showTabs}
        title={title}
      />
      <Box {...cardsListStyles} mt={!showTabs ? 3 : 0} pb={!lgUp && showTabs ? 7 : 0}>
        {renderCards()}
      </Box>
      <FiltersContainer
        filtersRef={filtersRef}
        handleClose={() => setFiltersOpen(false)}
        lgUp={lgUp}
        open={filtersOpen}
        viewBy={viewBy}
      />
    </Box>
  );
};

CardsPanel.propTypes = {
  activeMyAuthTab: string,
  currentSelection: shape({}),
  filterIdentifierCount: number.isRequired,
  filtersOpen: bool.isRequired,
  myAuthorizationCards: oneOfType([arrayOf(shape({})), shape({})]).isRequired,
  myAuthorizationPending: bool.isRequired,
  myAuthorizations: oneOfType([arrayOf(shape({})), shape({})]).isRequired,
  resetCardFilters: func.isRequired,
  selectorsListPending: bool.isRequired,
  setActiveMyAuthTab: func.isRequired,
  setFiltersOpen: func.isRequired,
  viewBy: string,
  viewById: string,
};

CardsPanel.defaultProps = {
  activeMyAuthTab: '',
  currentSelection: {},
  viewBy: '',
  viewById: '',
};

export default CardsPanel;
